import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "@/i18n/translations";
import { api } from "@/lib/api";

const AppContext = createContext(null);

// Compute a single cart line total based on sell_by.
export const lineTotal = (item) => {
  if (item.sell_by === "kg") {
    const g = Number(item.grams || 0);
    return (Number(item.price) * g) / 1000;
  }
  return Number(item.price) * Number(item.quantity || 1);
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("ikz_lang") || "tr");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ikz_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [profile, setProfile] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ikz_profile") || "null");
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    localStorage.setItem("ikz_lang", lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("ikz_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (profile) localStorage.setItem("ikz_profile", JSON.stringify(profile));
    else localStorage.removeItem("ikz_profile");
  }, [profile]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch {}
    })();
  }, []);

  const refreshSettings = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    return data;
  }, []);

  const t = useCallback(
    (key, vars = {}) => {
      const dict = translations[lang] || translations.tr;
      let val = dict[key] ?? key;
      Object.entries(vars).forEach(([k, v]) => {
        val = val.replace(`{${k}}`, v);
      });
      return val;
    },
    [lang]
  );

  // Add a product to the cart.
  // For unit products: qty is a piece count (default 1).
  // For kg products: pass { grams: <int> }; increments existing entry when passed twice.
  const addToCart = (product, opts = {}) => {
    const isKg = product.sell_by === "kg";
    setCart((prev) => {
      const ex = prev.find((i) => i.id === product.id);
      if (ex) {
        if (isKg) {
          const add = Number(opts.grams || product.default_grams || 500);
          return prev.map((i) =>
            i.id === product.id ? { ...i, grams: Number(i.grams || 0) + add } : i
          );
        }
        const add = Number(opts.qty || 1);
        return prev.map((i) =>
          i.id === product.id ? { ...i, quantity: Number(i.quantity || 0) + add } : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          name: lang === "tr" ? product.name_tr : product.name_en,
          price: product.price,
          image_url: product.image_url,
          sell_by: isKg ? "kg" : "unit",
          quantity: isKg ? 1 : Number(opts.qty || 1),
          grams: isKg ? Number(opts.grams || product.default_grams || 500) : null,
          min_grams: isKg ? Number(product.min_grams || 100) : null,
          step_grams: isKg ? Number(product.step_grams || 50) : null,
        },
      ];
    });
  };

  // Set absolute quantity (unit items) or grams (kg items).
  const updateQty = (id, value) => {
    setCart((prev) => {
      const it = prev.find((i) => i.id === id);
      if (!it) return prev;
      if (it.sell_by === "kg") {
        const g = Number(value);
        if (!g || g <= 0) return prev.filter((i) => i.id !== id);
        return prev.map((i) => (i.id === id ? { ...i, grams: g } : i));
      }
      const q = Number(value);
      if (!q || q <= 0) return prev.filter((i) => i.id !== id);
      return prev.map((i) => (i.id === id ? { ...i, quantity: q } : i));
    });
  };

  const removeFromCart = (id) =>
    setCart((prev) => prev.filter((i) => i.id !== id));

  const clearCart = () => setCart([]);

  const subtotal = cart.reduce((s, i) => s + lineTotal(i), 0);
  const minOrder = settings?.min_order || 400;
  const serviceFee = Number(settings?.service_fee ?? 40);
  // Free delivery above threshold; below it, add the service fee.
  const deliveryFee = subtotal > 0 && subtotal < minOrder ? serviceFee : 0;
  const total = subtotal + deliveryFee;
  const itemCount = cart.length;

  // meetsMin means "qualifies for free delivery". Orders below the threshold are
  // still allowed — they simply carry a service fee.
  const meetsMin = subtotal >= minOrder;
  const canOrder = cart.length > 0;

  const hour = new Date().getHours();
  const storeStatus = settings
    ? {
        storeOpen: hour >= settings.store_open_hour && hour < settings.store_close_hour,
        deliveryOpen:
          hour >= settings.delivery_start_hour && hour < settings.delivery_end_hour,
      }
    : { storeOpen: true, deliveryOpen: true };

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    try { localStorage.removeItem("ikz_session_token"); } catch {}
    setUser(null);
  };

  return (
    <AppContext.Provider
      value={{
        lang,
        setLang,
        t,
        cart,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        subtotal,
        deliveryFee,
        serviceFee,
        total,
        itemCount,
        profile,
        setProfile,
        settings,
        refreshSettings,
        minOrder,
        meetsMin,
        storeStatus,
        user,
        setUser,
        authLoading,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
